import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SpanishDatePicker from "@/components/SpanishDatePicker";
import { Trash2, Plus } from "lucide-react";

const DAYS = [
  ["MONDAY", "Lunes"], ["TUESDAY", "Martes"], ["WEDNESDAY", "Miércoles"],
  ["THURSDAY", "Jueves"], ["FRIDAY", "Viernes"], ["SATURDAY", "Sábado"], ["SUNDAY", "Domingo"],
];

export default function PhysioConfigDialog({ open, onOpenChange, config, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newUnavailable, setNewUnavailable] = useState("");
  const [newOpening, setNewOpening] = useState({ date: "", start_time: "19:00", end_time: "21:00" });

  useEffect(() => { if (open && config) setCfg(JSON.parse(JSON.stringify(config))); }, [open, config]);
  if (!cfg) return null;

  const toggleDay = (d) => setCfg((c) => ({
    ...c, days: c.days.includes(d) ? c.days.filter((x) => x !== d) : [...c.days, d],
  }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/physio/config", {
        days: cfg.days, start_time: cfg.start_time, end_time: cfg.end_time,
        slot_minutes: Number(cfg.slot_minutes) || 15, capacity_per_slot: Number(cfg.capacity_per_slot) || 1,
        responsible_person: cfg.responsible_person || "", timezone: "Europe/Madrid",
        unavailable_dates: cfg.unavailable_dates || [], extra_openings: cfg.extra_openings || [],
      });
      toast.success("Configuración guardada. Las citas existentes no se modifican.");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="physio-config-dialog">
        <DialogHeader>
          <DialogTitle>Configuración de Fisioterapia</DialogTitle>
          <DialogDescription>Los cambios solo afectan a turnos futuros; no mueven ni borran citas ya creadas.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Días de atención</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {DAYS.map(([k, lbl]) => (
                <button key={k} type="button" onClick={() => toggleDay(k)}
                  data-testid={`physio-cfg-day-${k}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                    cfg.days.includes(k) ? "bg-purple-700 text-white border-purple-700" : "bg-white text-slate-600 border-slate-200"}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Hora inicio</Label><Input type="time" value={cfg.start_time} onChange={(e) => setCfg({ ...cfg, start_time: e.target.value })} data-testid="physio-cfg-start" /></div>
            <div><Label>Hora fin</Label><Input type="time" value={cfg.end_time} onChange={(e) => setCfg({ ...cfg, end_time: e.target.value })} data-testid="physio-cfg-end" /></div>
            <div><Label>Duración (min)</Label><Input type="number" min="5" value={cfg.slot_minutes} onChange={(e) => setCfg({ ...cfg, slot_minutes: e.target.value })} data-testid="physio-cfg-duration" /></div>
            <div><Label>Capacidad por turno</Label><Input type="number" min="1" value={cfg.capacity_per_slot} onChange={(e) => setCfg({ ...cfg, capacity_per_slot: e.target.value })} data-testid="physio-cfg-capacity" /></div>
          </div>
          <div><Label>Persona responsable</Label><Input value={cfg.responsible_person || ""} onChange={(e) => setCfg({ ...cfg, responsible_person: e.target.value })} data-testid="physio-cfg-responsible" /></div>

          <div>
            <Label>Fechas no disponibles (festivos / vacaciones)</Label>
            <div className="flex gap-2 mt-1">
              <SpanishDatePicker value={newUnavailable} onChange={setNewUnavailable} testId="physio-cfg-unavailable-date" />
              <Button type="button" variant="outline" data-testid="physio-cfg-add-unavailable"
                onClick={() => { if (newUnavailable && !(cfg.unavailable_dates || []).includes(newUnavailable)) { setCfg({ ...cfg, unavailable_dates: [...(cfg.unavailable_dates || []), newUnavailable] }); setNewUnavailable(""); } }}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {(cfg.unavailable_dates || []).map((d) => (
                <span key={d} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-100 text-xs">
                  {d}
                  <button type="button" onClick={() => setCfg({ ...cfg, unavailable_dates: cfg.unavailable_dates.filter((x) => x !== d) })}><Trash2 className="h-3 w-3 text-rose-600" /></button>
                </span>
              ))}
            </div>
          </div>

          <div>
            <Label>Aperturas extraordinarias</Label>
            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 mt-1 items-end">
              <SpanishDatePicker value={newOpening.date} onChange={(v) => setNewOpening({ ...newOpening, date: v })} testId="physio-cfg-opening-date" />
              <Input type="time" className="w-24" value={newOpening.start_time} onChange={(e) => setNewOpening({ ...newOpening, start_time: e.target.value })} />
              <Input type="time" className="w-24" value={newOpening.end_time} onChange={(e) => setNewOpening({ ...newOpening, end_time: e.target.value })} />
              <Button type="button" variant="outline" data-testid="physio-cfg-add-opening"
                onClick={() => { if (newOpening.date) { setCfg({ ...cfg, extra_openings: [...(cfg.extra_openings || []), { ...newOpening }] }); setNewOpening({ date: "", start_time: "19:00", end_time: "21:00" }); } }}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {(cfg.extra_openings || []).map((o, i) => (
                <span key={`${o.date}-${i}`} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-100 text-xs">
                  {o.date} {o.start_time}–{o.end_time}
                  <button type="button" onClick={() => setCfg({ ...cfg, extra_openings: cfg.extra_openings.filter((_, j) => j !== i) })}><Trash2 className="h-3 w-3 text-rose-600" /></button>
                </span>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-purple-700 hover:bg-purple-800" data-testid="physio-cfg-save">
            {saving ? "Guardando…" : "Guardar configuración"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
